// src/features/rbac/components/UserRolesTable.tsx
import React, { useMemo } from 'react';
import { Plus, Minus }    from 'lucide-react';

import ExpandableTable    from '../../../../shared/components/ui/ExpandableTable';
import type { ColumnDef } from '../../../../shared/components/ui/MinimalisticTable';

import { useRoles }       from '../hooks/useRoles';
import {
  useUsersRoles,
  useRolesForUser,
}                         from '../hooks/useUserRoles';
import type { User }      from '../api/rbacApi';

/* ───────── sub-row (per user) ───────── */
function RolesSubRow(props: {
  user:   User;
  roles:  ReturnType<typeof useRoles>['data'];
  assign: ReturnType<typeof useUsersRoles>['assign'];
  remove: ReturnType<typeof useUsersRoles>['remove'];
}) {
  const { user, roles = [], assign, remove } = props;
  const { data: userRoles = [], isLoading }  = useRolesForUser(user.id_user);

  if (isLoading) return <p className="p-4 text-neutral-400">Loading…</p>;

  const assigned = new Set(userRoles);

  return (
    <div className="p-4 space-y-2">
      {roles.map((role) => {
        const hasRole = assigned.has(role.role_key);
        const Icon    = hasRole ? Minus : Plus;
        const action  = hasRole ? remove : assign;

        return (
          <div key={role.role_key} className="flex items-center gap-2">
            <Icon
              size={16}
              className={
                hasRole
                  ? 'cursor-pointer text-rose-400 hover:text-rose-300'
                  : 'cursor-pointer text-emerald-400 hover:text-emerald-300'
              }
              onClick={() =>
                action.mutate({ uid: user.id_user, role: role.role_key })
              }
            />
            <span className={hasRole ? 'text-neutral-100' : 'text-neutral-500'}>
              {role.role_key}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ───────── main table ───────── */
export default function UserRolesTable() {
  const { data: roles = [] }      = useRoles();
  const { users, assign, remove } = useUsersRoles();

  /* ← **ALL hooks first** */

  const showTenant = users.data?.some(
    (u) => u.tenant_id && u.tenant_id !== 0,
  );

  const columns: ColumnDef<User>[] = useMemo(() => {
    const cols: ColumnDef<User>[] = [
      { header: 'ID',       accessor: 'id_user'  },
      { header: 'Username', accessor: 'username' },
    ];
    if (showTenant) cols.push({ header: 'Tenant', accessor: 'tenant_id' });
    return cols;
  }, [showTenant]);

  /* ← THEN decide what to render */

  if (users.isLoading) return <p className="text-neutral-400">Loading…</p>;
  if (users.isError)   return <p className="text-rose-400">Error: {users.error?.message}</p>;

  return (
    <ExpandableTable
      columns={columns}
      data={users.data ?? []}
      noDataMessage="No users."
      showSearch
      rowKey={(u) => u.id_user}
      renderSubRow={(user) => (
        <RolesSubRow
          user={user}
          roles={roles}
          assign={assign}
          remove={remove}
        />
      )}
    />
  );
}
